import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { apiService } from "../services/api";
import AppointmentForm from "../Component/appointments/AppointmentForm";
import AppointmentsList from "../Component/appointments/AppointmentsList";
import format from 'date-fns/format';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import addDays from 'date-fns/addDays';
import isSameMonth from 'date-fns/isSameMonth';
import isSameDay from 'date-fns/isSameDay';
import parseISO from 'date-fns/parseISO';

const CalendarView = ({ appointments, selectedDate, onDateSelect, onAppointmentClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const nextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    onDateSelect(new Date());
  };

  const getAppointmentsForDate = (date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    return appointments.filter(apt => apt.date === dateString);
  };

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-xl shadow-sm border border-gray-100 sm:mb-6 sm:p-4 sm:rounded-2xl">
        <button 
          onClick={prevMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 group sm:p-3"
        >
          <svg className="w-4 h-4 text-gray-600 group-hover:text-blue-600 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <div className="text-center flex-1 mx-2">
          <h2 className="text-lg font-bold text-gray-800 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent sm:text-2xl">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <p className="text-xs text-gray-500 mt-1 hidden sm:block">Click on a date to view appointments</p>
          <p className="text-xs text-gray-500 mt-1 sm:hidden">Tap date to view</p>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <button 
            onClick={goToToday}
            className="px-2 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold text-xs sm:px-4 sm:py-2 sm:text-sm"
          >
            Today
          </button>
          <button 
            onClick={nextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 group sm:p-3"
          >
            <svg className="w-4 h-4 text-gray-600 group-hover:text-blue-600 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const days = [];
    const startDate = startOfWeek(currentDate);

    for (let i = 0; i < 7; i++) {
      const day = addDays(startDate, i);
      days.push(
        <div key={i} className="text-center py-2 font-semibold text-gray-600 text-xs uppercase tracking-wide bg-gray-50 rounded sm:py-3 sm:text-sm">
          {format(day, 'EEE')}
        </div>
      );
    }

    return <div className="grid grid-cols-7 gap-1 mb-1 sm:mb-2">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const dayAppointments = getAppointmentsForDate(cloneDay);
        const isToday = isSameDay(day, new Date());
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isSelected = selectedDate && isSameDay(day, selectedDate);

        days.push(
          <div
            className={`
              min-h-16 p-1 border border-gray-200 rounded-lg transition-all duration-200 cursor-pointer
              ${isCurrentMonth ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 text-gray-400'}
              ${isToday ? 'ring-1 ring-blue-500 ring-opacity-50 bg-blue-50' : ''}
              ${isSelected ? 'ring-1 ring-purple-500 bg-purple-50' : ''}
              ${dayAppointments.length > 0 ? 'hover:shadow-sm' : ''}
              sm:min-h-24 sm:p-2 sm:rounded-xl
            `}
            key={day.toString()}
            onClick={() => onDateSelect(cloneDay)}
          >
            <div className={`flex justify-between items-center mb-1 ${
              isToday ? 'font-bold text-blue-700' : isSelected ? 'font-bold text-purple-700' : 'text-gray-700'
            }`}>
              <span className="text-xs sm:text-sm">{format(day, 'd')}</span>
              {isToday && (
                <span className="text-xs bg-blue-500 text-white px-1 py-0.5 rounded-full sm:px-2 sm:py-1">Today</span>
              )}
            </div>
            
            <div className="space-y-0.5 max-h-10 overflow-y-auto sm:space-y-1 sm:max-h-16">
              {dayAppointments.slice(0, 2).map((apt, index) => (
                <div 
                  key={apt._id}
                  className="text-xs bg-gradient-to-r from-green-500 to-emerald-600 text-white p-0.5 rounded-md truncate cursor-pointer hover:from-green-600 hover:to-emerald-700 transition-all sm:p-1 sm:rounded-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAppointmentClick(apt);
                  }}
                  title={`${format(parseISO(`1970-01-01T${apt.time}`), 'h:mm a')} - ${apt.title}`}
                >
                  <div className="font-semibold truncate text-[10px] sm:text-xs">
                    {format(parseISO(`1970-01-01T${apt.time}`), 'h:mm a')}
                  </div>
                  <div className="truncate text-[10px] opacity-90 hidden sm:block">
                    {apt.title}
                  </div>
                </div>
              ))}
              {dayAppointments.length > 2 && (
                <div className="text-[10px] text-gray-500 text-center bg-gray-100 py-0.5 rounded sm:text-xs sm:py-1">
                  +{dayAppointments.length - 2} more
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7 gap-1 mb-1">
          {days}
        </div>
      );
      days = [];
    }

    return <div className="calendar-grid">{rows}</div>;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 border border-gray-100 sm:rounded-2xl sm:shadow-lg sm:p-6">
      {renderHeader()}
      {renderDays()}
      {renderCells()}
    </div>
  );
};

const AppointmentsPage = () => {
  const user = useSelector(state => state.user);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('list');
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false); // NEW: Track filter state

  useEffect(() => {
    fetchAppointments();
  }, []);

  useEffect(() => {
    // Only filter if we have a selected date that's not today
    const today = new Date();
    const isTodaySelected = selectedDate && isSameDay(selectedDate, today);
    
    if (selectedDate && !isTodaySelected) {
      const dateString = format(selectedDate, 'yyyy-MM-dd');
      const filtered = appointments.filter(apt => apt.date === dateString);
      setFilteredAppointments(filtered);
      setIsFiltered(true); // NEW: Set filtered state
    } else {
      setFilteredAppointments(appointments);
      setIsFiltered(false); // NEW: Clear filtered state
    }
  }, [selectedDate, appointments]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await apiService.getUserAppointments();
      if (response.success) {
        setAppointments(response.data);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      alert('Error loading appointments: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (appointment) => {
    if (editingAppointment) {
      setAppointments(prev => 
        prev.map(apt => apt._id === appointment._id ? appointment : apt)
      );
    } else {
      setAppointments(prev => [appointment, ...prev]);
    }
    setShowForm(false);
    setEditingAppointment(null);
    setSelectedDate(new Date(appointment.date));
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setShowForm(true);
  };

  const handleDelete = (appointmentId) => {
    setAppointments(prev => prev.filter(apt => apt._id !== appointmentId));
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAppointment(null);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setViewMode('list');
  };

  // FIXED: Show All function
  const handleShowAll = () => {
    setSelectedDate(new Date()); // Reset to today
    setIsFiltered(false); // Clear filter state
  };

  const handleAppointmentClick = (appointment) => {
    console.log('Appointment clicked:', appointment);
  };

  const getStats = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const todayAppointments = appointments.filter(apt => apt.date === today);
    const upcomingAppointments = appointments.filter(apt => new Date(apt.date) >= new Date());
    
    return {
      total: appointments.length,
      today: todayAppointments.length,
      upcoming: upcomingAppointments.length
    };
  };

  const stats = getStats();

  // Determine which appointments to display
  const displayAppointments = isFiltered ? filteredAppointments : appointments;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
      {/* Header */}
      <div className="text-center mb-4 sm:mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent sm:text-4xl sm:mb-3">
          My Appointments
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl mx-auto sm:text-lg">
          Manage and track all your scheduled appointments
        </p>
      </div>

      {!showForm ? (
        <div className="space-y-4 sm:space-y-8">
          {/* Stats Cards - Stack on mobile */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300 sm:p-6 sm:rounded-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs font-semibold sm:text-sm">Total Appointments</p>
                  <p className="text-xl font-bold mt-1 sm:text-3xl sm:mt-2">{stats.total}</p>
                </div>
                <div className="text-2xl sm:text-3xl">📊</div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300 sm:p-6 sm:rounded-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs font-semibold sm:text-sm">Today's Appointments</p>
                  <p className="text-xl font-bold mt-1 sm:text-3xl sm:mt-2">{stats.today}</p>
                </div>
                <div className="text-2xl sm:text-3xl">🎯</div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300 sm:p-6 sm:rounded-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs font-semibold sm:text-sm">Upcoming</p>
                  <p className="text-xl font-bold mt-1 sm:text-3xl sm:mt-2">{stats.upcoming}</p>
                </div>
                <div className="text-2xl sm:text-3xl">📅</div>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 sm:p-6 sm:rounded-2xl sm:shadow-lg">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center sm:gap-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <h2 className="text-lg font-bold text-gray-800 sm:text-2xl">
                  My Appointments <span className="text-blue-500">({appointments.length})</span>
                </h2>
                
                {/* Mobile View Toggle */}
                <div className="bg-gray-100 rounded-lg p-1 inline-flex w-full justify-center sm:hidden">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex-1 px-3 py-2 rounded-md font-semibold transition-all duration-300 text-xs ${
                      viewMode === 'list' 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    📋 List
                  </button>
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`flex-1 px-3 py-2 rounded-md font-semibold transition-all duration-300 text-xs ${
                      viewMode === 'calendar' 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    📅 Calendar
                  </button>
                </div>

                {/* Desktop View Toggle */}
                <div className="bg-gray-100 rounded-xl p-1 inline-flex hidden sm:inline-flex">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 text-sm ${
                      viewMode === 'list' 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    📋 List View
                  </button>
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 text-sm ${
                      viewMode === 'calendar' 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    📅 Calendar View
                  </button>
                </div>
              </div>

              <button
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-3 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold flex items-center justify-center gap-2 group text-sm sm:px-6 sm:py-3 sm:rounded-xl sm:text-base"
              >
                <svg className="w-4 h-4 group-hover:scale-110 transition-transform sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden xs:inline">New Appointment</span>
                <span className="xs:hidden">New</span>
              </button>
            </div>
          </div>

          {/* Content Area */}
          {loading ? (
            <div className="text-center py-8 bg-white rounded-xl shadow-sm sm:py-12 sm:rounded-2xl sm:shadow-lg">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-3 sm:h-16 sm:w-16 sm:mb-4"></div>
              <p className="text-gray-600 text-sm sm:text-lg">Loading your appointments...</p>
            </div>
          ) : viewMode === 'calendar' ? (
            <CalendarView 
              appointments={appointments}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              onAppointmentClick={handleAppointmentClick}
            />
          ) : (
            <>
              {/* Date Filter Info - Only show when filtered */}
              {isFiltered && filteredAppointments.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:rounded-2xl sm:p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-blue-800 font-semibold text-sm sm:text-base">
                      📅 Showing {filteredAppointments.length} appointment{filteredAppointments.length !== 1 ? 's' : ''} for {format(selectedDate, 'MMM d, yyyy')}
                    </p>
                    <button 
                      onClick={handleShowAll}
                      className="text-xs bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 transition-colors sm:text-sm"
                    >
                      Show All ({appointments.length})
                    </button>
                  </div>
                </div>
              )}

              {/* Show message when no appointments found for filtered date */}
              {isFiltered && filteredAppointments.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                  <p className="text-yellow-800 font-semibold">
                    📅 No appointments found for {format(selectedDate, 'MMM d, yyyy')}
                  </p>
                  <button 
                    onClick={handleShowAll}
                    className="mt-2 text-sm bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Show All Appointments
                  </button>
                </div>
              )}

              <AppointmentsList
                appointments={displayAppointments}
                onUpdate={handleEdit}
                onDelete={handleDelete}
              />
            </>
          )}

          {/* Calendar at Bottom - Hidden on mobile to save space */}
          <div className="mt-4 hidden sm:block sm:mt-8">
            <CalendarView 
              appointments={appointments}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              onAppointmentClick={handleAppointmentClick}
            />
          </div>

          {/* Mobile Floating Action Button */}
          <div className="fixed bottom-6 right-6 z-40 sm:hidden">
            <button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 font-semibold flex items-center justify-center"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <AppointmentForm
          onSuccess={handleSuccess}
          editAppointment={editingAppointment}
          onClose={handleCancel}
        />
      )}
    </div>
  );
};

export default AppointmentsPage;